<template>
    <div>
        <div :class="{'dropdown-content': !inline, 'dropdown-content-padded': !inline}">
            <div class="grid-content" v-if="comparison">
                <div class="grid-item grid-item-full grid-fitted">
                    <v-group-select v-if="comparison"
                                    label="&nbsp;"
                                    :options="comparisonOptions"
                                    :value="comparisonType"
                                    @update="updateComparison"/>
                </div>
            </div>
            <div class="grid-content">
                <div class="grid-item" :class="{'grid-item-full': !comparison}" v-if="showMinimum">
                    <v-date-input
                        label="Starting"
                        :type="datePickerType"
                        :value="min"
                        :validate="$v.min"
                        @input="updateMin"
                    />
                </div>
                <div class="grid-item" :class="{'grid-item-full': !comparison}" v-if="showMaximum">
                    <v-date-input
                        label="Ending"
                        :type="datePickerType"
                        :value="max"
                        :validate="$v.max"
                        @input="updateMax"
                    />
                </div>
                <div class="grid-item grid-item-full" v-if="!comparison">
                    <v-error-messages :has-frontend-errors="$v.$error">
                        <li class="list-item" v-if="!$v.min.optionalBetween || !$v.max.optionalBetween">The starting
                            date must be before or equal to the ending date.
                        </li>
                    </v-error-messages>
                </div>
            </div>
        </div>
        <slot v-if="$slots.footer" name="footer"/>
        <filter-footer v-else @update="updateFilter"/>
    </div>
</template>

<script>
    import vDateInput from '@/components/form/v-date-input';
    import vRadio from '@/components/form/v-radio';
    import vGroupSelect from '@/components/form/v-group-select';
    import vErrorMessages from '@/components/form/v-error-messages';
    import optionalBetween from '@/components/vuelidate/validators/optional-between';
    import _ from '@/common/helpers';
    import filterMixins from './filter-mixins';
    import FilterFooter from './filter-footer';

    const comparisonTypes = {
        between: 'between',
        before: 'before',
        after: 'after',
    };
    export default {
        name: 'datetime-filter',
        mixins: [filterMixins],
        props: {
            // show comparison radio buttons
            comparison: {
                type: Boolean,
                default: false,
            },
        },
        components: {
            vDateInput,
            vRadio,
            vGroupSelect,
            FilterFooter,
            vErrorMessages,
        },
        computed: {
            initialMinimum() {
                if (this.min === null) {
                    const values = this.adapter.getInitialFilterValue(this.filter);
                    return values ? values.min : null;
                }
                return this.min;
            },
            initialMaximum() {
                if (this.max === null) {
                    const values = this.adapter.getInitialFilterValue(this.filter);
                    return values ? values.max : null;
                }
                return this.max;
            },
            showMinimum() {
                return !this.comparison
                    || [
                        comparisonTypes.after,
                        comparisonTypes.between,
                    ].includes(this.comparisonType);
            },
            showMaximum() {
                return !this.comparison
                    || [
                        comparisonTypes.before,
                        comparisonTypes.between,
                    ].includes(this.comparisonType);
            },
            isTimePicker() {
                return this.filter.filter.options && this.filter.filter.options.options.timePicker;
            },
            datePickerType() {
                return this.isTimePicker ? 'datetime' : 'date';
            },
        },
        data() {
            return {
                comparisonType: comparisonTypes.between,
                comparisonOptions: Object.entries(comparisonTypes)
                    .map(([value, label]) => ({value, label: _.capitalize(label)})),
                min: null,
                max: null,
            };
        },
        created() {
            // fix init data error
            this.min = this.initialMinimum;
            this.max = this.initialMaximum;

            // update comparisonType
            const map = [
                [comparisonTypes.before, this.min === null && this.max !== null],
                [comparisonTypes.after, this.min !== null && this.max === null],
                [comparisonTypes.between, true],
            ];
            // eslint-disable-next-line
        this.comparisonType = map.find(v => v[1])[0];
        },
        validations() {
            return {
                /* eslint-disable func-names */
                min: {
                    optionalBetween: optionalBetween('max', function (min) {
                        return min.valueOf() <= this.max.valueOf();
                    }, false),
                },
                max: {
                    optionalBetween: optionalBetween('min', function (max) {
                        return max.valueOf() >= this.min.valueOf();
                    }, false),
                },
            };
        },
        methods: {
            makePayload() {
                return this.adapter.getFilterPayload({
                    index: this.index,
                    filter: this.filter,
                    data: this.preparePayloadData(),
                });
            },
            handleInput() {
                if (!this.inline) {
                    return;
                }
                this.$nextTick(() => {
                    this.$v.$touch();
                    if (!this.$v.$error) {
                        const payload = this.makePayload();
                        this.$emit('input', payload);
                    }
                });
            },
            update({min, max}) {
                this.min = min;
                this.max = max;
                this.handleInput();
            },
            updateMin(value) {
                const min = this.isTimePicker ? value : value.startOf('day');
                this.update({
                    min,
                    max: this.max,
                });
            },
            updateMax(value) {
                const max = this.isTimePicker ? value : value.endOf('day');
                this.update({
                    min: this.min,
                    max,
                });
            },
            updateFilter() {
                this.$v.$touch();
                if (!this.$v.$error) {
                    const payload = this.makePayload();
                    this.$emit('update', payload);
                    this.$parent.$emit('request-popper-hide');
                }
            },
            updateComparison(value) {
                this.comparisonType = value;

                if (value === comparisonTypes.before) {
                    this.update({min: null, max: this.max || this.$tz().now()});
                    return;
                }
                if (value === comparisonTypes.after) {
                    this.update({min: this.min || this.$tz().now(), max: null});
                    return;
                }
                this.update({min: null, max: null});
            },
            preparePayloadData() {
                const data = {min: null, max: null};
                if (_.isBlank(this.min)) {
                    data.max = this.max;
                } else if (_.isBlank(this.max)) {
                    data.min = this.min;
                } else {
                    data.min = this.min;
                    data.max = this.max;
                }
                return data;
            },
            focusFilter() {
                this.$el.querySelector('input').focus();
            },
        },
    };
</script>
